// Updated Price Estimation Tab – Aligned & Polished for NvisionAI
// ---
// This file contains the updated React component for the NvisionAI interface.
// The primary change is within the JSX for the 'priceestimation' tab, which
// has been restructured to match the consistent two-column layout of the other tabs.
// This ensures visual balance and a professional user experience.

// PDF GENERATION IMPORTS: Added 'useRef', 'jsPDF', 'html2canvas', and 'MdDownload'
import React, { useState, useEffect, useRef } from "react";
import "../styles/Product.css";
import axios from "axios";
// I've added MdDownload here for the PDF button
import { MdOutlineCloudUpload, MdDownload } from "react-icons/md";
import log1 from "../images/sample13.jpg";
import log2 from "../images/sample16.jpg";
import log3 from "../images/sample18.jpg";
import log4 from "../images/sample4.jpg";
import log5 from "../images/sample5.jpg";
import log6 from "../images/sample6.jpg";
import log7 from "../images/sample14.jpg";
import log8 from "../images/sample17.jpg";

// PDF GENERATION IMPORTS
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const sampleImages = [
  { url: log1, type: "image/jpg", alt: "image1" },
  { url: log2, type: "image/png", alt: "image2" },
  { url: log3, type: "image/jpeg", alt: "image3" },
  { url: log4, type: "image/jpeg", alt: "image4" },
  { url: log5, type: "image/jpeg", alt: "image5" },
  { url: log6, type: "image/jpeg", alt: "image6" },
  { url: log7, type: "image/jpeg", alt: "image7" },
  { url: log8, type: "image/jpeg", alt: "image8" },
];

export default function Product() {
  // Conversion rate: 1 USD = 83 INR (as of Oct 2025, update as needed)
  const INR_TO_USD = 83;
  // Helper to convert and format
  const inrToUsd = (inr) => {
    const num = parseFloat(inr);
    if (isNaN(num)) return "$ 0.00";
    return `$ ${(num / INR_TO_USD).toFixed(2)}`;
  };
  const [selectedImage, setSelectedImage] = useState(null);
  const [apiImage, setApiImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("product");
  const [isDragging, setIsDragging] = useState(false);
  const [typingAnimation, setTypingAnimation] = useState(false);
  const [activeButton, setActiveButton] = useState("product");
  const [uploadStatus, setUploadStatus] = useState("Upload an image");
  const [hideDownloadBtn, setHideDownloadBtn] = useState(false);
  const [progress, setProgress] = useState(0);

  // Get current Indian date (IST)
  const getIndianDate = () => {
    const now = new Date();
    // IST is UTC+5:30
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istTime = new Date(utc + 5.5 * 60 * 60 * 1000);
    // Format as DD/MM/YYYY
    const day = String(istTime.getDate()).padStart(2, "0");
    const month = String(istTime.getMonth() + 1).padStart(2, "0");
    const year = istTime.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Ref for PDF generation
  const estimateRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (validateFile(file)) {
      setSelectedImage(file);
      setApiImage(null);
    } else {
      alert(
        "Please choose a valid image file (jpg, jpeg, or png) under 700MB."
      );
    }
  };

  const validateFile = (file) => {
    return !!file;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragEnd = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const urlToFile = async (url, filename, mimeType) => {
    try {
      const response = await fetch(url);
      const data = await response.blob();
      return new File([data], filename, { type: mimeType });
    } catch (error) {
      console.error("Error converting URL to File:", error);
      return null;
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    let file = null;

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      file = event.dataTransfer.files[0];
    } else {
      const imageUrl = event.dataTransfer.getData("text/uri-list");
      if (imageUrl) {
        const draggedSample = sampleImages.find(
          (sample) => sample.url === imageUrl
        );
        const filename = draggedSample ? draggedSample.alt : "image.jpg";
        const mimeType = draggedSample ? draggedSample.type : "image/jpeg";
        file = await urlToFile(imageUrl, filename, mimeType);
      }
    }

    if (validateFile(file)) {
      setSelectedImage(file);
    } else {
      alert(
        "Please choose a valid image file (jpg, jpeg, or png) under 700MB."
      );
    }
  };

  const baseUrl =
    "https://4lzphexixj7m5puvjjd5gcf5pm0pfcbz.lambda-url.us-east-1.on.aws/";

  const handleFetchImage = async () => {
    if (!selectedImage) {
      setError("Please select an image.");
      return;
    }
    setLoading(true);
    setError(null);
    setUploadStatus("Uploading & Analyzing Image...");
    setProgress(0);
    const formData = new FormData();
    formData.append("image", selectedImage);

    // Animate progress: 0 -> 25% (10s), 25 -> 50% (10s), 50 -> 75% (10s), 100% on result
    let cancelled = false;
    const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
    try {
      setProgress(0);
      await sleep(100); // Small delay to visually reset
      setProgress(25);
      await sleep(10000);
      if (cancelled) return;
      setProgress(50);
      await sleep(10000);
      if (cancelled) return;
      setProgress(75);
      // Start upload after 25% (simulate real upload delay)
      const response = await axios.post(`${baseUrl}/Upload-Image`, formData);
      if (cancelled) return;
      if (response.data.message === "Upload relevant car image") {
        setApiImage(null);
        setUploadStatus("Please upload a relevant car image.");
        setProgress(0);
        alert("Please upload a relevant car image.");
      } else {
        const {
          car_brand,
          car_model,
          defects,
          detection_plot_url,
          total_estimated_repair_cost,
        } = response.data;

        const uploadedImageDetails = {
          id: 1,
          url: URL.createObjectURL(selectedImage),
          alt: "Uploaded Image",
        };

        const outputImageDetails = {
          id: 2,
          url: `${baseUrl}${detection_plot_url}?timestamp=${new Date().getTime()}`,
          alt: "AI Result",
          carBrand: car_brand,
          carModel: car_model,
          defects: defects,
          totalEstimatedCost: total_estimated_repair_cost,
        };

        setApiImage([uploadedImageDetails, outputImageDetails]);
        setUploadStatus("Analysis Complete!");
        setProgress(100);
      }
    } catch (err) {
      console.error("Error fetching image:", err);
      setError("Error uploading image. Please try again.");
      setUploadStatus("Upload failed");
      setProgress(0);
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 2000); // Reset progress after short delay
    }
  };

  const handleClear = () => {
    setSelectedImage(null);
    setApiImage(null);
    setError(null);
    setUploadStatus("Upload an image");
  };

  useEffect(() => {
    if (apiImage && apiImage.length > 1) {
      setTypingAnimation(true);
      setTimeout(() => {
        const resultContainer = document.getElementById("result-container");
        if (resultContainer) {
          resultContainer.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 500);
    }
  }, [apiImage]);

  const getUniqueDefects = (defects = []) => {
    const defectMap = {};
    defects.forEach((def) => {
      const name = def.defect_name.toLowerCase();
      if (!defectMap[name]) {
        defectMap[name] = { ...def };
      }
    });
    return Object.values(defectMap);
  };

  /**
   * --- NEW PDF DOWNLOAD HANDLER ---
   * This function uses html2canvas to capture the .estimate-wrapper div
   * and then uses jsPDF to save it as a PDF.
   */
  const handleDownloadPDF = () => {
    const input = estimateRef.current;
    if (!input) return;
    setHideDownloadBtn(true);
    setTimeout(() => {
      html2canvas(input, { scale: 2 })
        .then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "mm", "a4");
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;
          const ratio = canvasWidth / canvasHeight;
          let imgWidth = pdfWidth;
          let imgHeight = imgWidth / ratio;
          if (imgHeight > pdfHeight) {
            imgHeight = pdfHeight;
            imgWidth = imgHeight * ratio;
          }
          const x = (pdfWidth - imgWidth) / 2;
          const y = 0;
          pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
          pdf.save("NvisionAI_Estimate.pdf");
        })
        .catch((err) => {
          console.error("Error generating PDF:", err);
          alert("Could not generate PDF. Please try again.");
        })
        .finally(() => {
          setTimeout(() => setHideDownloadBtn(false), 100);
        });
    }, 100);
  };

  return (
    <section id="demo">
      <div className="demo-container">
        <div className="test-heading-container1">
          <h2 className="video-head">
            Experience <span className="nvision">NvisionAI</span>
          </h2>
        </div>
        <div className="app">
          <h3>{uploadStatus}</h3>
          <div
            className={`upload-container ${isDragging ? "dragging" : ""} image-upload-container`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragEnd}
            onDrop={handleDrop}
          >
            <div className="drop-zone">
              <div className="upload-icon">
                <MdOutlineCloudUpload />
              </div>
              <div className="drop-container">
                <p className="pa">
                  {selectedImage
                    ? selectedImage.name
                    : "Drag and drop image here"}
                </p>
                <p className="paragraph">
                  {selectedImage ? "" : "Limit 700mb per file. JPG, PNG, JPEG"}
                </p>
              </div>
            </div>
            <label className="browse-button">
              Browse
              <input
                type="file"
                className="input"
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
            </label>
          </div>
          <div className="action-buttons">
            <button
              onClick={handleFetchImage}
              disabled={loading || !selectedImage}
            >
              {loading ? "Processing..." : "Upload and Run"}
            </button>
            <button onClick={handleClear} className="clear-button">
              Clear
            </button>
          </div>
          {/* Progress Bar */}
          {loading && (
            <div style={{ width: "50%", margin: "10px auto", position: "relative" }}>
              <div
                style={{
                  height: "16px",
                  width: "100%",
                  background: "#e0e0e0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 1px 2px #ccc",
                  position: "relative",
                }}
              >
                {/* Milestone markers */}
                {[25, 50, 75, 100].map((milestone) => (
                  <div
                    key={milestone}
                    style={{
                      position: "absolute",
                      left: `calc(${milestone}% - 10px)`,
                      top: "-22px",
                      width: "20px",
                      textAlign: "center",
                      fontSize: "10px",
                      color: "#1879f7",
                      fontWeight: 600,
                    }}
                  >
                    {milestone}
                  </div>
                ))}
                <div
                  style={{
                    height: "100%",
                    width: `${progress}%`,
                    background: progress === 100 ? "#4caf50" : "#1879f7",
                    transition: "width 0.3s",
                    borderRadius: "8px",
                  }}
                ></div>
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontSize: "12px",
                  color: "#1879f7",
                  marginTop: "2px",
                }}
              >
                {progress}%
              </div>
            </div>
          )}
          <div
            id="result-container"
            className={`result-container ${typingAnimation ? "typing-animation" : ""}`}
          >
            {!apiImage && (
              <div className="result-container1">
                <h3>
                  Explore <span className="nvision">Our Featured Samples</span>
                </h3>
                <hr className="hr-line" />
                <div className="sample-image">
                  {sampleImages.map((sample, index) => (
                    <div
                      className="sample1"
                      key={index}
                      onClick={async () => {
                        const file = await urlToFile(
                          sample.url,
                          sample.alt,
                          sample.type
                        );
                        if (file) {
                          setSelectedImage(file);
                        }
                      }}
                    >
                      <img
                        src={sample.url}
                        alt={sample.alt}
                        className="sample"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {apiImage && apiImage.length > 1 && (
              <div className="output-container">
                <div className="output-buttons">
                  <button
                    className={activeButton === "product" ? "active" : ""}
                    onClick={() => {
                      setSelectedTab("product");
                      setActiveButton("product");
                    }}
                  >
                    Brand Identification
                  </button>
                  <button
                    className={
                      activeButton === "imageDetection" ? "active" : ""
                    }
                    onClick={() => {
                      setSelectedTab("imageDetection");
                      setActiveButton("imageDetection");
                    }}
                  >
                    Defect Detection
                  </button>
                  <button
                    className={
                      activeButton === "defectDetection" ? "active" : ""
                    }
                    onClick={() => {
                      setSelectedTab("defectDetection");
                      setActiveButton("defectDetection");
                    }}
                  >
                    Defect Categorization
                  </button>
                  <button
                    className={
                      activeButton === "priceestimation" ? "active" : ""
                    }
                    onClick={() => {
                      setSelectedTab("priceestimation");
                      setActiveButton("priceestimation");
                    }}
                  >
                    Price Estimation
                  </button>
                </div>
                <hr className="hr-line" />

                {/* --- Brand Identification TAB --- */}
                {selectedTab === "product" && (
                  <div className="result">
                    <div className="result-out">
                      <img
                        src={apiImage[0].url}
                        alt={apiImage[0].alt}
                        className="result-image"
                      />
                    </div>
                    <div className="vertical-line"></div>
                    <hr className="hr-line1" />
                    <div className="result-out">
                      <p className="result-description">
                        {apiImage[1].carBrand} - {apiImage[1].carModel}
                      </p>
                    </div>
                  </div>
                )}

                {/* --- Defect Detection TAB --- */}
                {selectedTab === "imageDetection" && (
                  <div className="result">
                    <div className="result-out">
                      <img
                        src={apiImage[0].url}
                        alt={apiImage[0].alt}
                        className="result-image"
                      />
                    </div>
                    <div className="vertical-line"></div>
                    <hr className="hr-line1" />
                    <div className="result-out">
                      <img
                        src={apiImage[1].url}
                        alt={apiImage[1].alt}
                        className="result-image"
                      />
                    </div>
                  </div>
                )}

                {/* --- Defect Categorization TAB --- */}
                {selectedTab === "defectDetection" && (
                  <div className="result">
                    <div className="result-out">
                      <img
                        src={apiImage[1].url}
                        alt={apiImage[1].alt}
                        className="result-image"
                      />
                    </div>
                    <div className="vertical-line"></div>
                    <hr className="hr-line1" />
                    <div className="result-out1">
                      {apiImage[1].defects && apiImage[1].defects.length > 0 ? (
                        <ul className="labels">
                          {getUniqueDefects(apiImage[1].defects).map(
                            (defect, index) => (
                              <li key={index} className="label">
                                {defect.defect_name.charAt(0).toUpperCase() +
                                  defect.defect_name.slice(1)}
                              </li>
                            )
                          )}
                        </ul>
                      ) : (
                        <p className="result-description">No defects found</p>
                      )}
                    </div>
                  </div>
                )}

                {/* --- PRICE ESTIMATION TAB (REFACTORED) --- */}
                {/* This block is entirely new and matches the provided PNG design */}
                {selectedTab === "priceestimation" &&
                  (getUniqueDefects(apiImage[1].defects).length === 0 ? (
                    <div className="result">
                      <div
                        className="result-out1"
                        style={{
                          width: "100%",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <p className="result-description">No defects found</p>
                      </div>
                    </div>
                  ) : (
                    <div className="result-out-full">
                      {/* The ref is attached to the wrapper div that will be captured */}
                      <div className="estimate-wrapper" ref={estimateRef}>
                        {/* Download Button inside the estimate page, top-right corner, no padding */}
                        <MdDownload
                          className={`download-pdf-btn${hideDownloadBtn ? " hide-for-pdf" : ""}`}
                          onClick={handleDownloadPDF}
                          title="Download Estimate as PDF"
                        />
                        {/* Header */}
                        <div className="estimate-header">
                          <div className="estimate-header-left">
                            <h2>NeuzenAI</h2>
                            <p>
                              T Hub Phase 2, 20, Inorbit Mall Rd, Vittal Rao
                              Nagar
                              <br />
                              Madhapur, Hyderabad, Telangana 500081
                            </p>
                            <strong>contact@neuzenai.com</strong>
                          </div>
                          <div className="estimate-header-right">
                            <h1>ESTIMATE</h1>
                          </div>
                        </div>

                        {/* Top Info Bar (Bill To / Invoice #) */}
                        <div className="estimate-info-bar">
                          <div className="estimate-info-left">
                            <strong>Bill To:</strong>
                            <p>
                              vansthalipuram, lbnagar
                              <br />
                              Hyderabad, TS 500064
                            </p>
                          </div>
                          <div className="estimate-info-right">
                            <p>
                              <span>#12345</span>
                            </p>
                            <p>
                              <span>{getIndianDate()}</span>
                            </p>
                          </div>
                        </div>

                        <div className="estimate-main-content">
                          {/* Left Column (Vehicle, Customer Info) */}
                          <div className="estimate-left-col">
                            <div className="estimate-info-block">
                              <strong>Vehicle Info:</strong>
                              {/* DYNAMIC DATA: Car Brand and Model */}
                              <p>{apiImage[1].carBrand}</p>
                              <p>{apiImage[1].carModel}</p>
                              {/* Hardcoded data from PNG */}
                              <p>2023</p>
                              <p>8,500 miles</p>
                              <p>TCX45678</p>
                              <p>VTS-2023</p>
                            </div>
                            <div className="estimate-info-block">
                              <strong>Customer Info:</strong>
                              <p>Stanford Balbaners</p>
                              <p>123 Main St, Townville</p>
                              <p>(222) 555-7777</p>
                              <p>stanford@email.com</p>
                            </div>
                            <div className="estimate-info-block">
                              <strong>Payment Method:</strong>
                              <p>Paypal: 1234567890</p>
                              <p>Payment: VISA, Master Card, etc.</p>
                            </div>
                          </div>

                          {/* Right Column (Services, Totals) */}
                          <div className="estimate-right-col">
                            <table className="estimate-table">
                              <thead>
                                <tr>
                                  <th>Services Performed</th>
                                  <th>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getUniqueDefects(apiImage[1].defects).map(
                                  (defect, index) => (
                                    <tr key={index}>
                                      <td>
                                        {defect.defect_name
                                          .charAt(0)
                                          .toUpperCase() +
                                          defect.defect_name.slice(1)}
                                      </td>
                                      <td>
                                        {inrToUsd(defect.estimated_cost.approx)}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>

                            <div className="estimate-totals">
                              <div className="estimate-totals-row">
                                <span>Sub Total</span>
                                {/* DYNAMIC DATA: Total Cost is used as Sub Total */}
                                <span>
                                  {inrToUsd(
                                    apiImage[1].totalEstimatedCost.approx
                                  )}
                                </span>
                              </div>
                              <div className="estimate-totals-row">
                                <span>Tax Rate</span>
                                <span>15%</span>
                              </div>
                              <div className="estimate-totals-divider"></div>
                              <div className="estimate-total-bill">
                                <strong>Total Bill</strong>
                                {/* DYNAMIC DATA: Calculated Total */}
                                <strong>
                                  {(() => {
                                    const base = parseFloat(
                                      apiImage[1].totalEstimatedCost.approx
                                    );
                                    if (isNaN(base)) return "$ 0.00";
                                    const total = base * 1.15;
                                    return `$ ${(total / INR_TO_USD).toFixed(2)}`;
                                  })()}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Footer (Terms) */}
                        <div className="estimate-footer">
                          <strong>Terms and Conditions:</strong>
                          <p>1. Total payment due in 30 days</p>
                          <p>
                            2. Please include the invoice number on your check
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        {error && (
          <p style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
